<template>
    <div
        :class="[$style['mds-modal'], classObject, widthClass, sizeClass]"
        :data-mds-version="version"
        v-overlay="{ visible: isVisible, class: classes, handler: 'hide' }"
        :aria-hidden="!isVisible"
        ref="modal-wrapper"
    >
        <transition
            :enter-active-class="$style['mds-modal-enter-active']"
            :leave-active-class="$style['mds-modal-leave-active']"
            :enter-class="$style['mds-modal-enter']"
            :leave-to-class="$style['mds-modal-leave-to']"
        >
            <div
                :class="$style['mds-modal__wrapper']"
                role="dialog"
                aria-modal="true"
                v-show="isVisible"
                v-bind="$attrs"
            >
                <div :class="$style['mds-modal__content']">
                    <slot />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import { MdsOverlay, MdsTrapFocus } from '@mds/utils-js';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsModal',
        inheritAttrs: false,
        props: {
            actionRequired: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, suppresses the ability to dismiss the modal with the `esc` key or by clicking the underlying page. An action in the modal must be used to dismiss.',
                },
            },
            disableFocusTrap: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, suppresses the built-in focus trap focus allowing you to leverage your own or third-party focus management.',
                },
            },
            firstFocusElement: {
                type: String,
                docs: {
                    validation: '—',
                    description:
                        'If class or id string is provided, overrides the first element for focus within the modal. Default is the modal itself.',
                },
            },
            fullscreen: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, renders the fullscreen variation of the modal.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the modal.',
                },
            },
            visible: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'Used to toggle the visibility of the modal.',
                    support: ['v-model'],
                },
            },
            width: {
                type: String,
                default: '900px',
                values: ['600px', '900px', '1200px'],
                validator(value) {
                    return ['600px', '900px', '1200px'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `600px`, `900px`, `1200px`.',
                    description: 'Sets the width of the modal.',
                },
            },
        },
        mixins: [MdsTrapFocus],
        directives: {
            overlay: MdsOverlay,
        },
        data() {
            return {
                classes: this.$style['mds-overlay'],
                isVisible: this.visible,
                version: `${name}-${version}`,
            };
        },
        watch: {
            visible(visible) {
                this[visible ? 'show' : 'hide']();
            },
        },
        model: {
            prop: 'visible',
            event: 'mds-modal-dismissed',
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-modal--fullscreen']] = this.fullscreen;

                return obj;
            },
            widthClass() {
                return this.$style[`mds-modal--width-${this.width}`];
            },
            sizeClass() {
                return this.$style[`mds-modal--${this.size}`];
            },
        },
        methods: {
            show(event) {
                this.isVisible = true;

                this.$nextTick(() => {
                    if (!this.disableFocusTrap) {
                        const focusElement = this.firstFocusElement
                            ? this.$refs['modal-wrapper'].querySelector(this.firstFocusElement)
                            : this.$refs['modal-wrapper'];

                        this.enableTrapFocus('modal-wrapper', focusElement);
                    }
                });

                this.$emit('mds-modal-opened', event);
            },
            hide(event) {
                // to negate duplicate dismiss event emit
                if (!this.isVisible) {
                    return;
                }

                if (
                    !this.disableFocusTrap &&
                    this.actionRequired &&
                    event &&
                    event.target.classList[0].includes('mds-directive--overlay__v3')
                ) {
                    this.enableTrapFocus(); // force focus back into modal
                    return;
                }

                if (!this.disableFocusTrap) {
                    this.disableTrapFocus();
                }

                this.isVisible = false;
                this.$emit('mds-modal-dismissed', this.isVisible, event);
            },
            modalKeyDownEvents(event) {
                if (!this.actionRequired && event.keyCode === 27) {
                    this.hide();
                }
            },
            bindEventHandlers() {
                window.addEventListener('keydown', this.modalKeyDownEvents, true);
            },
        },
        mounted() {
            this.bindEventHandlers();
        },
        destroyed() {
            window.removeEventListener('keydown', this.modalKeyDownEvents, true);
        },
    };
</script>

<style lang="scss" module>
    @import './modal.scss';
</style>
